/* eslint-disable no-param-reassign */
import React, { Dispatch, MutableRefObject, SetStateAction, useContext, useEffect, useRef, useState } from "react";

import Draggable, { DraggableData, DraggableEvent } from "react-draggable";
import { classnames } from "@culturehq/components";
import { ElementState, Preview } from "@creatomate/preview";
import { IconName } from "@culturehq/components/dist/components/Icon";

import { ElementPlacement } from "chq";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { useTimelineScale } from "components/stories/video-studio/contexts/TimelineScaleContext";
import { isSubtitleText } from "components/stories/video-studio/sidebar/tabs/subtitle/utils";

import { useActiveElementContext } from "../../context/ActiveElementContext";
import { DragContext } from "../../context/DragContext";
import { findSkippedElements, getBarStyle, getOtherActiveElements, getOtherElementsInSameTrack, movePlayhead, removeActiveElement, snapStart, validateDragMagnet, validateSameTrackOverlapping } from "../utils";
import { VIDEO_TYPE } from "../../../utils/constants";
import Container from "./Container";
import Bar from "../bar/Bar";
import { SMALL_ELEMENT, VIDEO_ELEMENT_HEIGHT } from "../Element";

const TIMELINE_HEIGHT = 40;

interface DraggableElementProps {
  barRef: MutableRefObject<HTMLDivElement>;
  currentElement: ElementState;
  onApplyPlacement: (trim?: boolean, startTrim?: boolean, trackIndex?: number) => Promise<void>;
  onContextMenu: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onSetGapPlacement: Dispatch<SetStateAction<ElementPlacement>>;
  onSetPlacement: (newPlacement: ElementPlacement) => void;
  onSetTranslateX: Dispatch<SetStateAction<number>>;
  placement: ElementPlacement;
  preview: Preview;
  translateX: number;
  trimming: boolean;
  withVideos: boolean;
}

const DraggableElement: React.FC<DraggableElementProps> = ({
  barRef,
  currentElement,
  onApplyPlacement,
  onContextMenu,
  onSetGapPlacement,
  onSetPlacement,
  onSetTranslateX,
  placement,
  preview,
  translateX,
  trimming,
  withVideos
}) => {
  const { data, handleUpdateData } = useContext(CreatomateContext);
  const { timelineScale } = useTimelineScale();
  const { activeElementsId } = data;
  const { draggingElement, setDraggingElement, setIsDragging } = useContext(DragContext);

  const active = activeElementsId.includes(currentElement.source.id);
  const activeElementContext = useActiveElementContext();
  const [wasActive, setWasActive] = useState(false);
  const [axis, setAxis] = useState<"both" | "x" | "y" | "none" | undefined>("both");
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const isMounted = useRef<boolean>(false);
  const [gridX, setGridX] = useState(1);
  const [magnetThreshold, setMagnetThreshold] = useState(0.25);
  const [_snapTime, setSnapTime] = useState(-1);
  const TIMELINE_SCALE_INITIAL_VALUE = 65;
  const MAGNET_INITIAL_THRESHOLD = 0.25;

  const [backgroundColor, setBackgroundColor] = useState("#3f4043");
  const [borderColor, setBorderColor] = useState("#3f4043");
  const [fontcolor, setFontColor] = useState("#3f4043");
  const [iconClass, setIconClass] = useState("camera");
  const [iconType, setIconType] = useState<IconName>("video-camera-filled");

  const [dragDirection, setDragDirection] = useState<"none" | "right" | "left">("none");
  const dragDirectionRef = useRef<"none" | "right" | "left">();
  dragDirectionRef.current = dragDirection;

  const isVideo = currentElement.source.type === VIDEO_TYPE;
  const isSmallElement = placement.duration * timelineScale < SMALL_ELEMENT;

  const isDraggingElement = currentElement.source.id === draggingElement?.source?.id;
  const isSubtitle = isSubtitleText(currentElement.source.name);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!active) setDragging(false);
  }, [active]);

  useEffect(
    () => {
      setAxis(activeElementsId.length > 1 ? "x" : "both");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeElementsId]
  );

  // the user stop dragging an active element
  useEffect(
    () => {
      if (active) {
        setDragging(activeElementContext.drag);
        if (!activeElementContext.drag && !isDraggingElement && activeElementContext.wasDrag) {
          if (barRef.current) {
            barRef.current.style.transform = "translate(0px, 0px)";
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeElementContext.drag]
  );

  useEffect(
    () => {
      const {
        backgroundColor,
        borderColor,
        fontColor,
        iconClass,
        iconType
      } = getBarStyle(active, currentElement);

      setBorderColor(borderColor);
      setBackgroundColor(backgroundColor);
      setFontColor(fontColor);
      setIconClass(iconClass);
      setIconType(iconType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentElement, active]
  );

  const manageSingleElementDrag = (newTime: number): boolean => {
    if (newTime < 0) {
      onSetGapPlacement(gp => ({ time: 0, duration: gp.duration }));
      setDragDirection("left");
      return false;
    }
    setDragDirection("none");
    return true;
  };

  const manageSkippingElement = (
    skippedElement: ElementState, draggingRight: boolean
  ): boolean => {
    const time = draggingRight
      ? skippedElement.localTime - currentElement.duration
      : skippedElement.localTime + skippedElement.duration;

    onSetGapPlacement(gp => ({ time, duration: gp.duration }));
    setDragDirection(draggingRight ? "right" : "left");
    return false;
  };

  const manageOtherSkippingElement = (
    draggingEl: ElementState, draggingNewTime: number, draggingRight: boolean
  ): boolean => {
    const draggingDiff = currentElement.localTime - draggingEl.localTime;
    onSetGapPlacement(gp => ({
      time: draggingNewTime + draggingDiff,
      duration: gp.duration
    }));
    setDragDirection(draggingRight ? "right" : "left");
    return false;
  };

  const manageOverlapping = (
    elementOverlapping: ElementState, draggingElement: ElementState, draggingRight: boolean
  ): boolean => {
    const isRight = draggingElement.localTime > elementOverlapping.localTime;
    const dragNewTime = isRight
      ? elementOverlapping.localTime + elementOverlapping.duration
      : elementOverlapping.localTime - draggingElement.duration;
    const dragDiff = draggingElement.localTime - currentElement.localTime;
    onSetGapPlacement(gp => ({ time: dragNewTime - dragDiff, duration: gp.duration }));
    setDragDirection(draggingRight ? "right" : "left");
    return false;
  };

  const manageNegativePlacement = (
    timeOffset: number, invalidTime: number, draggingRight: boolean, ucPlacement: number
  ): boolean => {
    const valid = currentElement.localTime > (invalidTime - timeOffset);
    onSetGapPlacement(gp => ({
      time: valid ? currentElement.localTime - (invalidTime - timeOffset) : ucPlacement,
      duration: gp.duration
    }));
    setDragDirection(draggingRight ? "right" : "left");
    return false;
  };

  const couldBeDragged = (
    activeElementsId: string[],
    currentElement: ElementState,
    elements: ElementState[],
    newTime: number,
    timeOffset: number
  ): boolean => {
    if (activeElementsId.length === 1) return manageSingleElementDrag(newTime);

    const draggingRight = newTime > placement.time;
    if (draggingRight && dragDirectionRef.current === "right") return false;
    if (!draggingRight && dragDirectionRef.current === "left") return false;

    const trackElements = getOtherElementsInSameTrack(activeElementsId, currentElement, elements);
    const otherDraggingElements = getOtherActiveElements(
      activeElementsId, currentElement, elements
    );
    const {
      skippedElementBackward,
      skippedElementForward
    } = findSkippedElements(currentElement, newTime, trackElements);

    // avoid active elements skipping backward and forward
    if (skippedElementBackward) return manageSkippingElement(skippedElementBackward, draggingRight);
    if (skippedElementForward) return manageSkippingElement(skippedElementForward, draggingRight);

    // verify all active elements are not skipping elements
    for (let i = 0; i < otherDraggingElements?.length; i += 1) {
      const draggingEl = otherDraggingElements[i];
      const trackElements = getOtherElementsInSameTrack(activeElementsId, draggingEl, elements);
      const draggingElementNewTime = draggingEl.localTime + timeOffset;
      const {
        skippedElementBackward,
        skippedElementForward
      } = findSkippedElements(draggingEl, draggingElementNewTime, trackElements);

      // avoid active elements skipping forward
      if (skippedElementForward) {
        const draggingNewTime = skippedElementForward.localTime - draggingEl.duration;
        return manageOtherSkippingElement(draggingEl, draggingNewTime, draggingRight);
      }

      // avoid active elements skipping backward
      if (skippedElementBackward) {
        const draggingNewTime = skippedElementBackward.localTime + skippedElementBackward.duration;
        return manageOtherSkippingElement(draggingEl, draggingNewTime, draggingRight);
      }
    }

    // verify all active elements are not overlapping
    for (let i = 0; i < otherDraggingElements?.length; i += 1) {
      const draggingEl = otherDraggingElements[i];
      const trackElements = getOtherElementsInSameTrack(activeElementsId, draggingEl, elements);
      const draggingElementNewTime = draggingEl.localTime + timeOffset;

      const overlappingEl = validateSameTrackOverlapping(
        trackElements,
        { time: draggingElementNewTime, duration: draggingEl.duration },
        draggingEl
      );

      if (overlappingEl) return manageOverlapping(overlappingEl, draggingEl, draggingRight);
    }

    const overlappingEl = validateSameTrackOverlapping(
      trackElements, { time: newTime, duration: currentElement.duration }, currentElement
    );

    // use case: element being drag is overlapping
    if (overlappingEl) return manageOverlapping(overlappingEl, currentElement, draggingRight);

    let ucPlacement = -1;
    if (newTime < 0) {
      onSetGapPlacement({ time: 0, duration: currentElement.duration });
      setDragDirection(draggingRight ? "right" : "left");
      ucPlacement = 0;
    }

    const times = otherDraggingElements.map(dragElement => dragElement.localTime + timeOffset);
    const invalidTime = times.find(time => time < 0);
    if (invalidTime) {
      return manageNegativePlacement(timeOffset, invalidTime, draggingRight, ucPlacement);
    }
    if (ucPlacement === 0) return false;

    setDragDirection("none");
    return true;
  };

  const handleStart = async (event: DraggableEvent, __: DraggableData) => {
    const newData: {
      activeElementsId?: string[];
      updateSubtitles: boolean;
    } = { updateSubtitles: true };
    let activeIds: string[] = [];
    if (!active) {
      if (event.metaKey || event.ctrlKey) {
        activeIds = [...activeElementsId, currentElement.source.id];
      } else {
        activeIds = [currentElement.source.id];
      }
      newData.activeElementsId = activeIds;
    }

    handleUpdateData(newData);
    setIsDragging(true);
    setMagnetThreshold((TIMELINE_SCALE_INITIAL_VALUE * MAGNET_INITIAL_THRESHOLD) / timelineScale);
    activeElementContext.setWasDrag(false);
    setWasActive(active);
    setDragging(true);
    activeElementContext.setDrag(true);

    if (!active) {
      if (event.metaKey || event.ctrlKey) {
        await preview.setActiveElements(activeIds);
      } else {
        await preview.setActiveElements(activeIds);
      }
    }

    setDraggingElement(currentElement);
    onSetGapPlacement({ time: -1, duration: -1 });
    setDragDirection("none");
  };

  const handleDrag = (_: DraggableEvent, data: DraggableData): void => {
    setAxis(activeElementsId.length > 1 ? "x" : "both");
    activeElementContext.setWasDrag(true);
    activeElementContext.setTransition(data.x);

    const timeOffset = data.x / timelineScale;
    let time = Math.max(currentElement.localTime + timeOffset, 0);

    const isDraggable = couldBeDragged(
      activeElementsId,
      currentElement,
      preview.state?.elements || [],
      currentElement.localTime + timeOffset,
      timeOffset
    );

    if (isDraggable) {
      onSetTranslateX(data.x);

      const doMagentEffect = validateDragMagnet(
        activeElementsId,
        currentElement,
        preview.state?.elements || [],
        magnetThreshold,
        setSnapTime,
        time
      );

      if (doMagentEffect) {
        setSnapTime(currentSnapTime => {
          if (currentSnapTime >= 0) {
            const startSnap = snapStart(time, currentElement.duration, currentSnapTime);
            time = startSnap ? currentSnapTime : currentSnapTime - currentElement.duration;
          }
          return currentSnapTime;
        });
        setGridX(15);
      } else {
        setGridX(1);
      }
      activeElementContext.setPlacement({ time, duration: currentElement.duration });
      onSetPlacement({ time, duration: currentElement.duration });
    } else {
      setAxis(activeElementsId.length === 1 ? "y" : "none");
      if (activeElementsId.length === 1) setPosition({ x: translateX, y: 0 });

      onSetGapPlacement(gp => {
        if (gp.time >= 0) {
          time = gp.time;
          const translateXToValue = (gp.time - currentElement.localTime) * timelineScale;
          onSetTranslateX(translateXToValue);
          activeElementContext.setTransition(translateXToValue);
          if (barRef.current) barRef.current.style.transform = `translate(${translateXToValue}px, 0px)`;
          activeElementContext.setPlacement({ time: gp.time, duration: currentElement.duration });
          onSetPlacement({ time, duration: currentElement.duration });
        }
        return gp;
      });
      if (barRef.current) {
        const currentY = activeElementsId.length === 1 ? data.y : 0;
        barRef.current.style.transform = `translate(${translateX}px, ${currentY}px)`;
      }
    }
  };

  const isAllowedMovement = (targetTrack: number): boolean => {
    const elements = preview.state?.elements || [];
    const elementsInTargetTrack = elements.filter(element => element.track === targetTrack);

    // block if is the subtitles track
    if (elementsInTargetTrack.some(element => isSubtitleText(element.source.name))) {
      return false;
    }

    const overlapping = elementsInTargetTrack.some(element => {
      const elementEndTime = element.globalTime + element.duration;
      const newEndtime = placement.time + placement.duration;
      return element.globalTime <= newEndtime && elementEndTime >= placement.time;
    });
    // block if there is another element in the same position
    return !overlapping;
  };

  const finishMovementVariables = () => {
    setDragging(false);
    activeElementContext.setDrag(false);
    onSetTranslateX(0);
    if (barRef.current) barRef.current.style.transform = "translate(0px, 0px)";
  };

  const handleStop = (event: DraggableEvent, data: DraggableData): void => {
    setPosition({ x: 0, y: 0 });
    activeElementContext.setAllowClick(true);
    setIsDragging(false);

    const allElements = preview.getElements();
    const tracksWithVids = allElements
      .filter(elm => elm.source.type === "video")
      .map(elm => elm.track)
      .filter((track, index, array) => array.indexOf(track) === index)
      .sort((a, b) => a - b);

    const tracksWithHiddenSubtitles = allElements
      .filter(elm => isSubtitleText(elm.source.name) && elm.source.visible === false)
      .map(elm => elm.track)
      .filter((track, index, array) => array.indexOf(track) === index)
      .sort((a, b) => a - b);

    const availableTracks = allElements
      .reduce((acc, elm) => {
        // Skip if we already processed this track
        if (acc.some(track => track.track === elm.track)) return acc;

        // skip if the track is hidden
        if (isSubtitleText(elm.source.name) && elm.source.visible === false) return acc;

        const height = tracksWithVids.includes(elm.track) ? VIDEO_ELEMENT_HEIGHT : TIMELINE_HEIGHT;
        return [...acc, { track: elm.track, height }];
      }, [] as Array<{ track: number; height: number }>)
      .sort((a, b) => a.track - b.track);

    const currentTrackIndex = availableTracks.findIndex(at => at.track === currentElement.track);

    const rounded = Math.round(data.y / TIMELINE_HEIGHT);
    const integer = Math.floor(Math.abs(rounded)) * Math.sign(rounded);
    if (integer !== 0 && (axis === "both" || axis === "y") && !isSubtitle) {
      const timeOffset = data.x / timelineScale;
      const newTime = Math.max(currentElement.localTime + timeOffset, 0);
      const rawIndex = currentTrackIndex - integer;
      const targetIndex = rawIndex < 0 ? 0 : rawIndex;
      let desireTrackIndex = -1;
      let integerTracker = 0;

      if (tracksWithVids.length > 0) {
        if (integer < 0) {
          for (let i = currentTrackIndex + 1; i <= availableTracks.length - 1; i += 1) {
            const hasVideo = availableTracks[i].height === VIDEO_ELEMENT_HEIGHT;

            integerTracker -= hasVideo ? 2 : 1;

            if (integerTracker <= integer) {
              desireTrackIndex = i;
              i = availableTracks.length;
            }
          }
        } else {
          for (let i = currentTrackIndex - 1; i >= 0; i -= 1) {
            const hasVideo = availableTracks[i].height === VIDEO_ELEMENT_HEIGHT;

            integerTracker += hasVideo ? 2 : 1;

            if (integerTracker >= integer) {
              desireTrackIndex = i;
              i = -1;
            }
          }
        }
      }

      if (!isAllowedMovement(0)) {
        activeElementContext.setAllowClick(false);
        onApplyPlacement();
        finishMovementVariables();
        return;
      }
      let finalTrack = desireTrackIndex !== -1
        ? availableTracks[desireTrackIndex].track : targetIndex;

      // user wants to position dragged element on a new track at the top
      if (targetIndex > (availableTracks.length - 1)) {
        finalTrack = availableTracks[availableTracks.length - 1].track + 1;
      }

      const trackHasSubtitles = isSubtitleText(
        allElements.find(element => element.track === finalTrack)?.source.name);

      if (!trackHasSubtitles) {
        onApplyPlacement(false, false, finalTrack);
      } else {
        onApplyPlacement();
      }
    } else if (activeElementContext.wasDrag) {
      activeElementContext.setAllowClick(false);
      onApplyPlacement();
    }

    // move playhead just when there is a single element selected
    if (activeElementsId.length === 1) movePlayhead(preview, placement.time);

    finishMovementVariables();

    if (!activeElementContext.wasDrag) {
      if (event.metaKey || event.ctrlKey) {
        activeElementContext.setAllowClick(false);
        if (wasActive) {
          if (activeElementsId.length !== 1) {
            preview.setActiveElements(
              removeActiveElement(activeElementsId, currentElement.source.id)
            );
          }
        } else {
          preview.setActiveElements([...activeElementsId, currentElement.source.id]);
        }
      } else {
        preview.setActiveElements([currentElement.source.id]);
      }
    }
  };

  const handleTimelineElementsClick = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): Promise<void> => {
    if (!activeElementContext.allowClick) return;

    const svgRect = event.currentTarget.getBoundingClientRect();
    const clickX = Math.max(0, event.clientX - svgRect.left);
    const seekto = (clickX / timelineScale) + placement.time;
    if (seekto >= 0 && seekto <= (preview.state?.duration || 0)) await preview.setTime(seekto);
  };

  return (
    <Draggable
      axis={isSubtitle ? "x" : axis}
      defaultPosition={{ x: 0, y: 0 }}
      position={position}
      grid={[gridX, TIMELINE_HEIGHT]}
      scale={1}
      onStart={(event, data) => { handleStart(event, data); }}
      onDrag={handleDrag}
      onStop={handleStop}
    >
      <Container
        onClick={handleTimelineElementsClick}
        onContextMenu={onContextMenu}
        className={classnames({ narrowBar: isSmallElement }, "TimelineElement")}
        ref={barRef}
        active={active}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        color={fontcolor}
        dragging={dragging}
        style={{
          left: dragging
            ? currentElement.globalTime * timelineScale
            : placement.time * timelineScale,
          opacity: currentElement.source.visible === false ? 0.5 : 1,
          width: placement.duration * timelineScale
        }}
        withVideos={withVideos}
        isVideo={isVideo}
        activeElementsLength={activeElementsId.length}
        smallElement={isSmallElement}
      >
        <Bar
          currentElement={currentElement}
          iconClass={iconClass}
          iconType={iconType}
          isSmallElement={isSmallElement}
          isVideo={isVideo}
          placement={placement}
          trimming={trimming}
          withVideos={withVideos}
        />
      </Container>
    </Draggable>
  );
};

export default DraggableElement;
